import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { BROCHURE, PROCESS } from "@/data/content";
import { Icon, Section, SectionHeading } from "@/components/common";
import Reveal from "@/components/Reveal";


export const ServiceVisual = ({ service }) => {
  const nodes = {
    "ai-data": ["Data platforms", "AI & analytics", "Better decisions"],
    "digital-transformation": ["Strategy", "Connected systems", "Modern operations"],
    "software-product": ["Product design", "Build & integrate", "Web · Mobile · SaaS"],
    "managed-it-talent": ["Specialist teams", "Managed services", "Ongoing support"],
  }[service.id];
  return <div className="flex h-full min-h-[320px] flex-col justify-center bg-[#F6F5F2] p-8 sm:p-12" data-testid={`service-visual-${service.id}`}>
    <p className="mb-8 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">From possibility to practice</p>
    <div className="relative flex flex-col items-center gap-5">
      <div className="absolute bottom-8 top-8 w-px bg-slate-300" aria-hidden="true" />
      {nodes.map((node, i) => <div key={node} className={`relative flex w-full max-w-sm items-center gap-4 rounded-lg border px-6 py-5 ${i === 1 ? "border-[#262A30] bg-[#262A30] text-white" : "border-slate-200 bg-white text-slate-700"}`}>
        {i === 1 ? <Icon name={service.icon} className="h-6 w-6 text-[#F08089]" /> : <span className="text-xs text-brand-violet">0{i + 1}</span>}
        <span className="font-display text-lg font-semibold">{node}</span>
      </div>)}
    </div>
    <p className="mt-8 text-xs text-slate-500">{service.name}</p>
  </div>;
};

export const CapabilityDiagram = () => (
  <div className="capability-diagram" data-testid="capability-diagram">
    <div className="flex items-center justify-between border-b border-slate-200 pb-5">
      <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">One connected operation</span>
      <ArrowUpRight className="h-5 w-5 text-brand-violet" />
    </div>
    {[
      { icon: "chart", label: "Boardroom", desc: "Decisions · Business value", width: "w-full" },
      { icon: "brain", label: "Intelligence", desc: "AI · Analytics · Automation", width: "w-[90%]" },
      { icon: "network", label: "Connected systems", desc: "IT + OT · Cloud · Data", width: "w-[80%]" },
      { icon: "factory", label: "Plant floor", desc: "Assets · Sensors · Operations", width: "w-[70%]" },
    ].map((layer, index) => (
      <div key={layer.label} className={`capability-layer ${layer.width}`} data-testid={`capability-layer-${index}`}>
        <Icon name={layer.icon} className="h-6 w-6 shrink-0" />
        <div><p className="font-display text-lg font-bold">{layer.label}</p><p className="mt-1 text-xs opacity-80">{layer.desc}</p></div>
      </div>
    ))}
    <p className="mt-5 flex items-center gap-3 text-xs text-slate-500"><span className="h-px flex-1 bg-slate-300" />From sensor to boardroom<span className="h-px flex-1 bg-slate-300" /></p>
  </div>
);

export const PortfolioSection = () => (
  <Section className="py-20 sm:py-24">
    <Reveal><SectionHeading eyebrow="The complete portfolio" title="Connect the plant floor" gradient="to the enterprise." subtitle="Strategy, connected operations and long-term support — organised around your transformation journey." /></Reveal>
    <div className="mt-12 grid gap-px overflow-hidden rounded-2xl border border-slate-200 bg-slate-200 lg:grid-cols-3" data-testid="brochure-portfolio">
      {BROCHURE.portfolio.map((pillar, i) => (
        <div key={pillar.id} className="bg-white p-7 sm:p-9" data-testid={`portfolio-${pillar.id}`}>
          <div className="flex items-center justify-between"><Icon name={pillar.icon} className="h-8 w-8 text-brand-violet" /><span className="font-display text-4xl text-slate-200">0{i + 1}</span></div>
          <h3 className="mt-8 font-display text-2xl font-bold">{pillar.name}</h3>
          <ul className="mt-6 space-y-4">{pillar.items.map((item) => <li key={item} className="flex gap-3 text-sm leading-relaxed text-slate-600"><span className="mt-2 h-1 w-1 shrink-0 bg-brand-violet" />{item}</li>)}</ul>
          <Link to="/contact" className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-brand-violet" data-testid={`portfolio-cta-${pillar.id}`}>Discuss your needs <ArrowUpRight className="h-4 w-4" /></Link>
        </div>
      ))}
    </div>
  </Section>
);

export const ProcessGraphic = () => (
  <div className="border-y border-slate-200 bg-[#F6F5F2]" data-testid="delivery-process">
    <Section className="py-20 sm:py-24">
      <Reveal><SectionHeading eyebrow="How we work" title="From strategy" gradient="to sustained value." subtitle="A four-stage transformation journey, backed by a structured 12-stage project execution lifecycle." /></Reveal>
      <ol className="process-track mt-14 grid gap-8 md:grid-cols-2 lg:grid-cols-4">
        {PROCESS.map((step) => <li key={step.step} className="relative" data-testid={`process-step-${step.step}`}>
          <div className="relative z-10 mb-7 flex h-14 w-14 items-center justify-center rounded-full bg-[#262A30] text-white"><Icon name={step.icon} className="h-6 w-6" /></div>
          <span className="text-xs font-semibold tracking-widest text-brand-violet">{step.step}</span>
          <h3 className="mt-2 font-display text-xl font-bold">{step.title}</h3>
          <p className="mt-3 text-sm leading-relaxed text-slate-600">{step.desc}</p>
        </li>)}
      </ol>
      <p className="mt-12 flex items-center gap-3 border-t border-slate-200 pt-6 text-sm text-slate-600"><ArrowRight className="h-5 w-5 shrink-0 text-brand-violet" />Collaborative partnership and knowledge transfer create long-term success.</p>
    </Section>
  </div>
);
